import axios from 'axios';

// Matches the backend's fallback in GcmController
export const DEFAULT_GCM_LOCATION = 'Minneapolis, MN';

export const getGcmMerchants = async ({ location = DEFAULT_GCM_LOCATION, search = '', cardPrograms = '' } = {}) => {
    const params = new URLSearchParams();
    if (location)     params.append('location', location);
    if (search)       params.append('search', search);
    if (cardPrograms) params.append('card_programs', cardPrograms);

    const response = await axios.get(`/gcm/merchants?${params.toString()}`);
    return response.data;
};

// Browser-side IP lookup: the service sees the shopper's public IP (the backend only
// sees a proxy or 127.0.0.1) and its rate limit applies per shopper, not per server
const lookupFromBrowser = async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    try {
        const response = await fetch('https://ipapi.co/json/', { signal: controller.signal });
        const data = await response.json();
        if (data?.error || !data?.city || !data?.region_code) return null;
        return `${data.city}, ${data.region_code}`;
    } catch (_) {
        return null;
    } finally {
        clearTimeout(timer);
    }
};

// The backend answers with the default when its own lookup fails, so the default
// can't be told apart from a real detection — treat it as "not detected"
const lookupFromBackend = async () => {
    try {
        const response = await axios.get('/gcm/location');
        const location = response.data?.location;
        return location && location !== DEFAULT_GCM_LOCATION ? location : null;
    } catch (_) {
        return null;
    }
};

// { location, detected }: `detected` is false when falling back to the default
export const detectGcmLocation = async () => {
    const location = (await lookupFromBrowser()) || (await lookupFromBackend());
    return location
        ? { location, detected: true }
        : { location: DEFAULT_GCM_LOCATION, detected: false };
};

// Device location via the browser (GPS/Wi-Fi). Only offered in secure contexts
// (HTTPS or localhost); resolves to "lat,lng", which the GCM search accepts.
export const canUseDeviceLocation = () =>
    typeof navigator !== 'undefined' && 'geolocation' in navigator && window.isSecureContext;

export const getDeviceLocation = () => new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
        ({ coords }) => resolve(`${coords.latitude.toFixed(4)},${coords.longitude.toFixed(4)}`),
        reject,
        { timeout: 10000, maximumAge: 5 * 60 * 1000 }
    );
});
